<template>
    <div>
        <div class="modal-overlay active" @click="closeDialog()"></div>
        <div ref="dynamicDialog" class="modal specsheet-model">
            <div class="modal__header">
                <h2 class="headline-s">Spec Sheet</h2>
                <CloseIcon @click="closeDialog()" class="modal__header--close" alt="Close Modal" aria-label="Close" />
            </div>
            <div class="modal__body flex-column">
               
                <div class="input-folder">
                    <div class="input-folder--input">
                        <PrimeInputText
                            id="emailInput" v-model="form.email" placeholder=" "
                            :class="{'invalid-state-input': form.errors?.email != null}"
                            fluid
                        />
                        <label for="emailInput">
                            <p class="paragraph-default">Email *</p>
                        </label>
                    </div>
                    <div class="input-folder">
                        <p class="input-support-text" v-if="!form.errors?.email">* Email address is only required to email the spec sheet</p>
                        <p class="input-support-text invalid-state" v-if="form.errors?.email">{{ form.errors.email }}</p>
                    </div>
                </div>
                <Recaptcha v-if="!isLoggedIn" ref="recaptcha" v-model="captchaResponse" theme="light" />
                <div class="flex-row-forced" style="gap: 1rem; flex-flow: row wrap;">
                    <RothcoCta id="submitButton" :class="emailValid ? `primary` : `disabled`" @click="sendEmail">Email Me</RothcoCta>
                    <RothcoCta id="submitButton" :class="captchaValid ? `primary` : `disabled`" @click="viewSpecSheet">View</RothcoCta>
                    <RothcoCta id="submitButton" :class="captchaValid ? `primary` : `disabled`" @click="download">Download</RothcoCta>
                </div>
            </div>
        </div>
    </div>    
</template>
<script lang="ts" setup>
    import { inject, ref, computed } from "vue";
    import { onClickOutside } from "@vueuse/core";
    import Recaptcha from "@/components/Recaptcha.vue";
    import RothcoCta from "@/components/RothcoCta.vue";
    import { useSettingsStore } from "@/store/settings";
    import { useFetch } from "@/helpers/composables/useFetch";
    import { storeToRefs } from "pinia";
    import { useForm } from "@/helpers/composables/useForm";
    import { string, object } from "yup";
    import CloseIcon from "@icons/closeIcon.svg";
    let settings = useSettingsStore();

    let { user } = storeToRefs(settings);
    let { setOverlayMessage, setNotificationMessage} = settings;

    const dialogRef = inject('dialogRef');
    let dynamicDialog = ref();

    let spec_sheet = dialogRef?.value?.data?.spec_sheet ?? null;

    let form = useForm({
        useInertia: false,
        schema: object({
            email: string().email('Must be a valid email').required("Email is required for receiving an email").default(''),
        })
    });

    let recaptcha = ref();
    let captchaResponse = ref();

    let isLoggedIn = computed(()=>{
        return user.value?.user_id != null;
    });

    let captchaValid = computed(()=>{
        if(isLoggedIn.value) return true;
        return captchaResponse.value != null && captchaResponse.value != "";
    });

    let emailValid = computed(()=>{
        return spec_sheet != null && spec_sheet != '' && captchaValid.value &&
            form.email != null && form.email != '' && (form.errors?.email == null || form.errors?.email == '');
    });

    const closeDialog = () => {
        dialogRef?.value?.close();
    };

    const buildRequestBody = (extra: Record<string, unknown> = {}) => ({
        'g-recaptcha-response': captchaResponse.value ?? '',
        ...extra,
    });

    const specSheetFetchOptions = {
        beforeFetch(ctx: any) {
            ctx.options.headers = {
                ...ctx.options.headers,
                'Accept': 'application/json, application/pdf',
                'X-Requested-With': 'XMLHttpRequest',
            };
            return ctx;
        },
    };

    const filenameFromResponse = (response: Response | undefined, fallback: string): string => {
        const disposition = response?.headers.get('Content-Disposition') ?? '';
        const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
        return match?.[1] ? decodeURIComponent(match[1]) : fallback;
    };

    const viewSpecSheet = async () => {
        if (!captchaValid.value) return;
        const pdfWindow = window.open('', '_blank');
        const { data, response } = await useFetch(spec_sheet, specSheetFetchOptions)
            .post(buildRequestBody())
            .blob();

        if (!response.value?.ok || !data.value) {
            pdfWindow?.close();
            setNotificationMessage({
                message: 'Error viewing spec sheet. Please try again.',
                type: 'error',
            });
            return;
        }

        const blobUrl = URL.createObjectURL(data.value);
        if (pdfWindow) {
            pdfWindow.location.href = blobUrl;
        } else {
            window.open(blobUrl, '_blank');
        }
        closeDialog();
    };

    const sendEmailLoading = ref(false);
    const sendEmail = async () => {
        if (!emailValid.value || sendEmailLoading.value) return;
        sendEmailLoading.value = true;
        closeDialog();
        setOverlayMessage('Processing your request. Please wait...');

        const { response } = await useFetch(spec_sheet, specSheetFetchOptions)
            .post(buildRequestBody({ email: form.email }))
            .json();

        sendEmailLoading.value = false;
        setOverlayMessage(null);

        if (!response.value?.ok) {
            setNotificationMessage({
                message: 'Error sending email. Please try again.',
                type: 'error',
            });
            return;
        }

        setNotificationMessage({
            message: 'Email has been sent. Please check your inbox.',
            type: 'success',
        });
    };

    const download = async () => {
        if (!captchaValid.value) return;

        const { data, response } = await useFetch(spec_sheet, specSheetFetchOptions)
            .post(buildRequestBody({ download: 1 }))
            .blob();

        if (!response.value?.ok || !data.value) {
            setNotificationMessage({
                message: 'Error downloading spec sheet. Please try again.',
                type: 'error',
            });
            return;
        }

        const blobUrl = URL.createObjectURL(data.value);
        const filename = filenameFromResponse(response.value, 'specsheet.pdf');
        const anchor = document.createElement('a');
        anchor.href = blobUrl;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        closeDialog();
    };

    onClickOutside(dynamicDialog, ()=>{
        closeDialog();
    });

</script>
